// How dynamic column widths are handled: ============================
// Every cell in the table is wrapped by the DynamicWidthCell component.
// When each cell renders, their width is shared with table.js using the
// setColumnWidth callback. In setColumnWidth, we don't directly update the
// columnWidths object as it can lead to way too many renders of this
// component; one render for each cell. In a table with 10 columns and 10
// rows, that's probably going to be 100 renders. To avoid too many renders,
// we update the ref columnWidthsBufferRef. We use a recursive setTimeout
// to check when columnWidthsBufferRef was last updated. If it's found
// to be updated more than 500ms ago, then we assume that there are no
// more cells left for which width is to be measured. We then commit the
// columnWidthsBufferRef to columnWidths which causes a render and the column
// widths are updated in the UI correctly.

import { DataGridPro } from '@mui/x-data-grid-pro';
import { LicenseInfo } from '@mui/x-license-pro';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import * as React from 'react';

import NoData from '../common/NoData';
import DynamicWidthCell from './dynamic-width-cell';
import './table.scss';
import TableSkeleton from './skeleton';
import { Box } from '@mui/material';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_KEY);

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 15, 25, 50, 100];

const getDetailPanelHeight = () => 'auto';

// PERF: Stable overlay components extracted outside the component
// to prevent MUI DataGrid from unmounting/remounting overlay slots
const DetailPanelExpandIconStable = () => <div></div>;

function LoadingOverlayStable({ skeletonRowHeight, skeletonTableHeight }) {
  return (
    <Box
      position={'absolute'}
      zIndex={5}
      top={-55}
      left={0}
      height={'calc( 100% + 110px )'}
      width={'calc( 100% + 110px )'}>
      <TableSkeleton skeletonRowHeight={skeletonRowHeight} height={skeletonTableHeight} />
    </Box>
  );
}
const LoadingOverlayStableMemo = React.memo(LoadingOverlayStable);

export default function MuiTable({
  columns,
  rows,
  components = {},
  expandRow = undefined,
  onRowClick,
  pagination = true,
  onTableChange,
  hideFooter = false,
  total_size,
  rowHeight = 80,
  noBorder,
  headerHeight,
  disableRowsPerPage,
  rowsPerPageOptions,
  pinnedColumns, // array of fields, e.g. ['name', 'environment_id']
  LoaderState = false,
  paginationMode, // server
  page, // for paginationMode === 'server, start from 1
  onPageChange,
  pageSize,
  onPageSizeChange,
  setPageSizeExternal,
  dynamicHeight = {},
  pointerOnHover,
  getDetailPanelContent,
  noDataMessage,
  disableVerticalScroll,
  getRowId,
  ref,
  expandedRowIds,
  checkboxSelection = false,
  sortingMode = 'client',
  handleSortModelChange = () => {},
  selectedRow,
  customClassName,
  disableDynamicColumn = false,
  isDynamicHeight = false,
  tableHeight,
  isCardTable = false,
  hoverScrollbar = false,
  onRowMouseEnter,
  onRowMouseLeave,
  cursorPagination = false,
  skeletonRowHeight = 49.9,
  skeletonTableHeight = '100%',
  pinnedRows,
  componentsProps,
  // Opt-in: tell the grid the real `headerHeight`. MUI X v5 ignores `columnHeaderHeight` and lays the rows
  // out under a 56px header, so a shorter header leaves the rows area too short and an empty strip below it.
  syncHeaderHeight = false,
  // borderLeft,
  ...otherProps
}) {
  // PERF: Dark mode styling now handled in table.scss via html[data-theme='dark']

  const mergedComponentsProps = React.useMemo(() => {
    return merge(
      {
        pagination: {
          SelectProps: {
            MenuProps: { disablePortal: true },
          },
        },
      },
      componentsProps,
    );
  }, [componentsProps]);
  const [_pageSize, setPageSize] = React.useState(pageSize || DEFAULT_PAGE_SIZE);
  const [columnWidths, setColumnWidths] = React.useState({}); // { [index]: number }
  const columnWidthsBufferRef = React.useRef(columnWidths);
  const columnWidthsBufferLastUpdatedAtRef = React.useRef(undefined);
  const [error, setError] = React.useState(false);

  // PERF: Pre-compute Sets of pinned row IDs for O(1) lookups
  const pinnedIdSets = React.useMemo(() => {
    if (!pinnedRows) return { topSet: null, bottomSet: null };
    const getId = r => (getRowId ? getRowId(r) : r.id);
    const topSet = new Set((pinnedRows.top || []).map(getId));
    const bottomSet = new Set((pinnedRows.bottom || []).map(getId));
    return { topSet, bottomSet };
  }, [pinnedRows, getRowId]);

  const _rows = React.useMemo(() => {
    if (!pinnedRows) return rows;
    const combinedRows = [...rows];
    const topRows = pinnedRows.top || [];
    const bottomRows = pinnedRows.bottom || [];
    const rowIdSet = new Set(rows.map(r => (getRowId ? getRowId(r) : r.id)));

    topRows.forEach(pr => {
      const prId = getRowId ? getRowId(pr) : pr.id;
      if (!rowIdSet.has(prId)) {
        combinedRows.unshift(pr);
      }
    });

    bottomRows.forEach(pr => {
      const prId = getRowId ? getRowId(pr) : pr.id;
      if (!rowIdSet.has(prId)) {
        combinedRows.push(pr);
      }
    });

    return combinedRows;
  }, [rows, pinnedRows, getRowId]);
  // Define which columns are restricted
  React.useEffect(() => {
    if (!rows || !Array.isArray(rows)) {
      setError(true);
    } else {
      setError(false);
    }
  }, [rows]);

  // PERF: Memoize to avoid new array allocation every render
  const restrictedColumnsArray = React.useMemo(
    () => (columns?.length >= 2 ? [columns[0].field, columns[1].field] : []),
    [columns],
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState(() => {
    const visibility = {};
    columns?.forEach(column => {
      visibility[column.field] = true;
    });
    return visibility;
  });

  const handleColumnVisibilityModelChange = newModel => {
    const updatedModel = { ...newModel };
    restrictedColumnsArray?.forEach(column => {
      updatedModel[column] = true; // Ensure restricted columns are always visible
    });
    setColumnVisibilityModel(updatedModel);
  };

  // PERF: bulkMode was always false (setter never used), so isRowSelectable is always () => false
  const isRowSelectable = React.useCallback(() => false, []);

  const [columnWidthsInitialized, setColumnWidthsInitialized] = React.useState(false);

  const initialState = React.useMemo(
    () => ({
      ...(pinnedColumns?.length && { pinnedColumns: { left: pinnedColumns } }),
      ...(pinnedRows && { pinnedRows }),
      detailPanel: { expandedRowIds: expandedRowIds },
    }),
    [expandedRowIds, pinnedColumns, pinnedRows],
  );

  const setColumnWidth = React.useCallback(({ columnIndex, width }) => {
    columnWidthsBufferRef.current = {
      ...columnWidthsBufferRef.current,
      [columnIndex]: Math.max(width + 20, columnWidthsBufferRef.current[columnIndex] || 0),
    }; // 20 is the left and right paddings combined
    columnWidthsBufferLastUpdatedAtRef.current = Date.now();
  }, []);

  React.useEffect(() => {
    if (page && _pageSize && columns && rows) {
      // Keep this empty if block to ensure the effect is triggered for these deps changing.
    }

    columnWidthsBufferLastUpdatedAtRef.current = undefined;
    columnWidthsBufferRef.current = {};
    setColumnWidthsInitialized(false);
    setColumnWidths({});

    let invalidated = false;

    // PERF: Use requestIdleCallback instead of recursive setTimeout
    // This prevents blocking the main thread during column width calculations
    const checkAndCommitColumnWidthsBuffer = () => {
      if (invalidated) return;

      if (columnWidthsBufferLastUpdatedAtRef.current === undefined) {
        // Still waiting for first measurement
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => checkAndCommitColumnWidthsBuffer(), { timeout: 100 });
        } else {
          setTimeout(checkAndCommitColumnWidthsBuffer, 50);
        }
      } else {
        // PERF: Reduced debounce from 500ms to 300ms for faster feedback
        if (Date.now() - columnWidthsBufferLastUpdatedAtRef.current >= 300) {
          // Commit the column widths buffer
          setColumnWidths(columnWidthsBufferRef.current);
          setColumnWidthsInitialized(true);
        } else {
          if (window.requestIdleCallback) {
            window.requestIdleCallback(() => checkAndCommitColumnWidthsBuffer(), { timeout: 100 });
          } else {
            setTimeout(checkAndCommitColumnWidthsBuffer, 50);
          }
        }
      }
    };

    // Start the check after a brief delay to allow initial render
    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => checkAndCommitColumnWidthsBuffer(), { timeout: 100 });
    } else {
      setTimeout(checkAndCommitColumnWidthsBuffer, 50);
    }

    return () => {
      invalidated = true;
    };
  }, [page, _pageSize, columns, rows]);

  // PERF: Use a ref to scope hover delegation to this table instance
  // instead of global document.querySelector searches
  const tableContainerRef = React.useRef(null);

  // Handle row hover events
  React.useEffect(() => {
    if (!onRowMouseEnter || !onRowMouseLeave) return;

    let hoverTimeoutRef = null;
    let currentHoveredRowRef = null;

    const handleMouseOver = event => {
      const rowElement = event.target.closest('.MuiDataGrid-row');
      if (rowElement) {
        const rowIndex = rowElement.getAttribute('data-rowindex');
        if (rowIndex !== null && rows && rows[parseInt(rowIndex)]) {
          const rowData = rows[parseInt(rowIndex)];

          // Clear any existing timeout
          if (hoverTimeoutRef) {
            clearTimeout(hoverTimeoutRef);
          }

          // Only trigger if it's a different row
          if (currentHoveredRowRef !== rowData) {
            // If we're moving to a different row, immediately cleanup the previous row
            if (currentHoveredRowRef) {
              onRowMouseLeave();
            }

            currentHoveredRowRef = rowData;
            onRowMouseEnter(event, rowData);
          }
        }
      }
    };

    const handleMouseOut = event => {
      const rowElement = event.target.closest('.MuiDataGrid-row');
      if (rowElement) {
        // Check if we're still within the same row
        const relatedTarget = event.relatedTarget;
        if (!relatedTarget || !rowElement.contains(relatedTarget)) {
          // Debounced mouse leave to prevent flickering
          if (hoverTimeoutRef) {
            clearTimeout(hoverTimeoutRef);
          }

          hoverTimeoutRef = setTimeout(() => {
            if (currentHoveredRowRef) {
              onRowMouseLeave();
              currentHoveredRowRef = null;
            }
          }, 50); // Reduced delay for better responsiveness
        }
      }
    };

    // PERF: Use the ref directly instead of querying the global DOM
    const tableElement = tableContainerRef.current;
    if (tableElement) {
      tableElement.addEventListener('mouseover', handleMouseOver);
      tableElement.addEventListener('mouseout', handleMouseOut);
    }

    return () => {
      if (tableElement) {
        tableElement.removeEventListener('mouseover', handleMouseOver);
        tableElement.removeEventListener('mouseout', handleMouseOut);
      }

      // Clean up timeout on unmount
      if (hoverTimeoutRef) {
        clearTimeout(hoverTimeoutRef);
      }
    };
  }, [rows, onRowMouseEnter, onRowMouseLeave]);

  const _columns = React.useMemo(() => {
    return columns.map((c, index) => {
      const { minWidth, width, ...column } = c;
      return {
        ...column,
        minWidth: width || minWidth || columnWidths[index], // width and minWidth specified in the columns definition take precedence over the dynamic column width
        renderHeader: params => {
          const content = params.colDef.headerName || '';

          return (
            <DynamicWidthCell
              columnIndex={index}
              previousWidth={columnWidthsInitialized ? columnWidths[index] : 0}
              setColumnWidth={setColumnWidth}>
              {column.renderHeader ? column.renderHeader(params) : content}
            </DynamicWidthCell>
          );
        },
        renderCell: params => {
          const content = params.formattedValue || '';
          return (
            <DynamicWidthCell
              columnIndex={index}
              previousWidth={columnWidthsInitialized ? columnWidths[index] : 0}
              setColumnWidth={setColumnWidth}
              useCharacterLimit={column.useCharacterLimit}
              copyData={column.copyData}
              field={column.headerName}
              params={params}
              // columnWidths={columnWidths[index]}
              disableAbsolutePositioning={Boolean(width || minWidth)}>
              {column.renderCell ? column.renderCell(params) : content}
            </DynamicWidthCell>
          );
        },
      };
    });
  }, [columnWidths, columnWidthsInitialized, columns, setColumnWidth]);

  const handlePageChange = React.useCallback(
    p => {
      // +1 because mui table pages start from 0
      onPageChange(p + 1);
      if (onTableChange) onTableChange(p + 1);
    },
    [onPageChange, onTableChange],
  );

  const handlePageSizeChange = React.useCallback(
    newPageSize => {
      setPageSize(newPageSize);
      if (onPageSizeChange) {
        onPageSizeChange(newPageSize);
      }
    },
    [onPageSizeChange],
  );

  const _getDetailPanelContent = React.useCallback(
    row => (row?.row && expandRow ? expandRow(row.row) : {}),
    [expandRow],
  );

  // PERF: Removed lodash calls - this function runs for every row
  const getRowClassName = React.useCallback(
    params => {
      const row = params.row;
      const rowId = getRowId ? getRowId(row) : row.id;

      // Build severity class
      let severityClass = '';
      if (row.shield_risk_severity) {
        severityClass = `bl-${row.shield_risk_severity.toLowerCase()}`;
      } else if (row.shield_severity) {
        severityClass = `bl-${row.shield_severity.toLowerCase()}`;
      } else if (typeof row.severity === 'string') {
        severityClass = `bl-${row.severity.toLowerCase()}`;
      } else if (row.cq_shield_priority) {
        severityClass = `bl-${row.cq_shield_priority.toLowerCase()}`;
      } else if (row.rule_severity) {
        severityClass = `bl-${row.rule_severity.toLowerCase()}`;
      } else if (typeof row.risk === 'string') {
        severityClass = `bl-${row.risk.toLowerCase()}`;
      }

      // Build scan status class
      const scanStatusClass = row.scan_status ? `bl-${row.scan_status.toLowerCase()}` : '';

      // Build show-critical class
      const showCritical =
        row.show_configuration || row.classification === 'Critical' || row.status || row.edited_by
          ? 'show-critical'
          : '';

      // Build pinned class - PERF: O(1) Set lookup instead of O(n) .some()
      let pinnedClass = '';
      if (pinnedIdSets.topSet?.has(rowId)) {
        pinnedClass = 'pinned-row-top';
      } else if (pinnedIdSets.bottomSet?.has(rowId)) {
        pinnedClass = 'pinned-row-bottom';
      }

      // Build selected class - use simple reference equality or ID comparison instead of _.isEqual
      const isSelected =
        selectedRow && (selectedRow === row || (getRowId ? getRowId(selectedRow) === rowId : selectedRow.id === rowId));

      return `${severityClass} ${scanStatusClass} ${showCritical} bl-base ${pointerOnHover ? 'pointer-on-hover' : ''} ${isSelected ? 'selected-row' : ''} ${pinnedClass}`.trim();
    },
    [pointerOnHover, selectedRow, pinnedIdSets, getRowId],
  );

  const tableStyle = React.useMemo(() => {
    // PERF: Most styles are now in table.scss
    // Only dynamic CSS custom properties and height calculations here
    let _height = '63.7vh';
    if (rowHeight) {
      _height = rowHeight * 10 + 118;
    } else {
      _height = 49.9 * 10 + 118;
    }

    return {
      ...(isDynamicHeight && { height: _height }),
      ...(tableHeight && { height: tableHeight }),
      ...dynamicHeight,
      // CSS custom properties for SCSS
      '--muitable-row-height': `${rowHeight}px`,
      '--muitable-header-height': headerHeight || '49.9px',
      // Minimal inline styles that can't be in SCSS
      '& div.MuiDataGrid-columnHeaders': {
        minHeight: headerHeight ? `${headerHeight} !important` : '49.9px !important',
        maxHeight: headerHeight ? `${headerHeight} !important` : '49.9px !important',
      },
      '& .MuiDataGrid-virtualScroller': {
        marginTop: headerHeight ? `${headerHeight} !important` : '49.9px !important',
      },
    };
  }, [rowHeight, isDynamicHeight, tableHeight, dynamicHeight, headerHeight]);
  const ErrorTable = React.useCallback(
    () => (
      <DataGridPro
        className={`muiTable__custom ${customClassName} ${noBorder ? 'muiTable__custom--noBorder' : ''} ${
          disableVerticalScroll ? 'muiTable__custom--disableVerticalScroll' : ''
        }`}
        sx={tableStyle}
        columns={columns}
        rows={[]}
        components={{ NoRowsOverlay: () => <NoData message={noDataMessage} height="100%" /> }}
        hideFooter={hideFooter}
        columnHeaderHeight={49.9}
      />
    ),
    [columns, customClassName, disableVerticalScroll, hideFooter, noBorder, noDataMessage, tableStyle],
  );

  // PERF: Create stable overlay component references to prevent MUI slot remounts
  const NoRowsOverlayStable = React.useCallback(
    () => <NoData message={noDataMessage} height="100%" />,
    [noDataMessage],
  );

  const ErrorOverlayStable = React.useCallback(
    () => <ErrorTable />,
    [ErrorTable],
  );

  const LoadingOverlayWrapped = React.useCallback(
    () => <LoadingOverlayStableMemo skeletonRowHeight={skeletonRowHeight} skeletonTableHeight={skeletonTableHeight} />,
    [skeletonRowHeight, skeletonTableHeight],
  );

  const _components = React.useMemo(
    () =>
      Object.keys(components).length > 0
        ? {
            NoRowsOverlay: NoRowsOverlayStable,
            ErrorOverlay: ErrorOverlayStable,
            LoadingOverlay: LoadingOverlayWrapped,
            ...components,
          }
        : {
            DetailPanelExpandIcon: DetailPanelExpandIconStable,
            NoRowsOverlay: NoRowsOverlayStable,
            ErrorOverlay: ErrorOverlayStable,
            LoadingOverlay: LoadingOverlayWrapped,
          },
    [ErrorOverlayStable, NoRowsOverlayStable, LoadingOverlayWrapped, components],
  );

  // PERF: Memoize className to avoid string concatenation every render
  const gridClassName = React.useMemo(
    () =>
      `muiTable__custom ${customClassName || ''} ${noBorder ? 'muiTable__custom--noBorder' : ''} ${disableVerticalScroll ? 'muiTable__custom--disableVerticalScroll' : ''} ${LoaderState ? 'muiTable__custom--loading' : ''} ${onRowClick ? 'muiTable__custom--clickableRows' : ''} ${isCardTable ? 'muiTable__custom--cardTable' : ''} ${isCardTable && hoverScrollbar ? 'muiTable__custom--hoverScrollbar' : ''}`.trim(),
    [customClassName, noBorder, disableVerticalScroll, LoaderState, onRowClick, isCardTable, hoverScrollbar],
  );

  // PERF: Memoize merged sx to avoid new object allocation every render
  const mergedSx = React.useMemo(
    () => ({ ...tableStyle, ...otherProps.sx }),
    [tableStyle, otherProps.sx],
  );

  try {
    return (
      <div ref={tableContainerRef} style={{ height: '100%', width: '100%' }}>
      <DataGridPro
        className={gridClassName}
        sx={mergedSx}
        columns={disableDynamicColumn ? columns : _columns}
        loading={LoaderState}
        checkboxSelection={checkboxSelection}
        rows={_rows}
        ref={ref}
        getRowId={getRowId}
        getDetailPanelHeight={getDetailPanelHeight}
        getDetailPanelContent={getDetailPanelContent}
        isRowSelectable={isRowSelectable}
        components={_components}
        rowThreshold={0}
        header
        {...(expandRow && { getDetailPanelContent: _getDetailPanelContent })}
        // Modified pagination props for cursor pagination
        pagination={cursorPagination ? false : pagination}
        hideFooterPagination={cursorPagination}
        {...(paginationMode && !cursorPagination && { paginationMode })}
        {...(page !== undefined && !cursorPagination && { page: page - 1 })}
        {...(onPageChange && !cursorPagination && { onPageChange: handlePageChange })}
        hideFooter={hideFooter}
        onPageSizeChange={handlePageSizeChange}
        pageSize={setPageSizeExternal || pageSize || _pageSize}
        {...(disableRowsPerPage
          ? null
          : rowsPerPageOptions
            ? { rowsPerPageOptions }
            : { rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS })}
        onRowClick={onRowClick}
        getRowClassName={getRowClassName}
        rowHeight={rowHeight}
        rowCount={total_size} // Use -1 for cursor pagination
        sortingMode={sortingMode}
        onSortModelChange={handleSortModelChange}
        initialState={initialState}
        disableVirtualization={false}
        disableColumnMenu={true}
        columnBufferPx={400}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        columnHeaderHeight={49.9}
        {...(syncHeaderHeight && headerHeight && { headerHeight: parseFloat(headerHeight) })}
        error={error}
        pinnedRows={pinnedRows}
        componentsProps={mergedComponentsProps}
        {...omit(otherProps, 'sx')}
        // Use slots for custom footer when cursor pagination is enabled
      />
      </div>
    );
  } catch (error) {
    console.error('Error while loading the table', error);
    return <ErrorTable />;
  }
}

export { DEFAULT_PAGE_SIZE, DEFAULT_ROWS_PER_PAGE_OPTIONS };
